/**
 * Centralized ERP Validation Configuration
 *
 * This file is the single source of truth for every ERP-specific rule:
 *  - Vista line-type definitions (Type 1 – 8)
 *  - Which fields are enabled / disabled based on the selected line type
 *  - Header-level (account-detail) validations per ERP
 *  - Line-item-level validations per ERP
 *  - Field-disabled state helpers consumed by canvas / coddingFields / lineItems
 *
 * Usage:
 *   import { validateAccountDetailsByErp, validateLineItemsByErp, isFieldDisabledByErp } from '../services/erpValidationConfig';
 */

import { isEmpty } from 'lodash';

/* ───────────────────────────────────────────────────
   1b.  VISTA TYPE-BASED RULES (the `type` field)
   ─────────────────────────────────────────────────── */

// Field names use codeContent / line-item naming (userJobId, userPhases, etc.)
const VISTA_TYPE_RULES = {
  1: {
    label: 'Job',
    required: ['userJobId', 'userPhases', 'userCostType'],
    notAllowed: ['workOrder', 'scope', 'equipmentNo']
  },
  2: {
    label: 'Inventory',
    required: ['itemReference'],
    notAllowed: ['userJobId', 'userPhases', 'workOrder', 'scope', 'equipmentNo']
  },
  3: {
    label: 'Expense',
    required: ['glAccount'],
    notAllowed: ['userJobId', 'userPhases', 'workOrder', 'scope', 'equipmentNo']
  },
  4: {
    label: 'Equipment',
    required: ['equipmentNo', 'userCostId', 'userCostType'],
    notAllowed: ['userJobId', 'userPhases', 'workOrder', 'scope']
  },
  5: {
    label: 'EM Work Order',
    required: ['workOrder', 'equipmentNo', 'scope'],
    notAllowed: ['userJobId', 'userPhases'] //NOT SURE ABOUT SCOPE
  },
  6: {
    label: 'PO',
    required: ['glAccount'],
    notAllowed: [
      'userJobId',
      'userPhases',
      'userCostType',
      'userCostId',
      'workOrder',
      'scope',
      'equipmentNo'
    ]
  },
  7: {
    label: 'Subcontract',
    required: ['glAccount'],
    notAllowed: [
      'userJobId',
      'userPhases',
      'userCostType',
      'userCostId',
      'workOrder',
      'scope',
      'equipmentNo'
    ]
  },
  8: {
    label: 'SM Work Order',
    required: ['workOrder', 'scope', 'userCostType'],
    notAllowed: ['userJobId', 'userPhases', 'GlAccount', 'glAccount', 'equipmentNo']
  }
};

const getVistaTypeNum = (type) => {
  if (!type) return null;
  const m = String(type).match(/^(\d+)/);
  return m ? m[1] : null;
};

// The rules name GL account `glAccount` at the header and `GlAccount` on a line item; the
// document field configuration carries the one name `GlAccount`. Every other rule field is
// spelled the same in both places.
const RULE_FIELD_CONFIG_NAME = {
  glAccount: 'GlAccount'
};

// Is this rule field one the company still carries? Callers that don't know — anything
// without a field configuration to hand — get `true`, which is the old behaviour: every
// rule field enforced.
const isRuleFieldOn = (isFieldOn, fieldName) =>
  typeof isFieldOn !== 'function' || isFieldOn(RULE_FIELD_CONFIG_NAME[fieldName] || fieldName);

// A field switched off in Document Configurations never renders and never receives a
// value, so no line type may require it: Type 1 (Job) with the job field off left Verify
// All disabled for good, with nothing on screen to fill in. Drop those fields from the
// rule instead — a type whose every required field is off requires only the type itself.
// `notAllowed` is untouched: a field that cannot be entered is already not entered.
const withConfiguredFields = (rule, isFieldOn) => {
  if (!rule || typeof isFieldOn !== 'function') return rule;
  const required = (rule.required || []).filter((name) => isRuleFieldOn(isFieldOn, name));
  return required.length === (rule.required || []).length ? rule : { ...rule, required };
};

export const getVistaTypeRule = (type, vistaBatchType, isFieldOn) => {
  const num = getVistaTypeNum(type);
  if (!num) return null;
  const rule = VISTA_TYPE_RULES[num];
  if (!rule) return null;
  // UnApproved Vista batches allow partial coding, since the batch is completed in
  // Vista before it posts. Job (Type 1) keeps Job Id required — Phase and Cost Type
  // are not — and every other type requires nothing at all. `notAllowed` is untouched:
  // a field the type forbids stays disabled either way. Batch type defaults to
  // Approved, so only an explicit 'UnApproved' relaxes the rules.
  if (vistaBatchType === 'UnApproved') {
    return withConfiguredFields({ ...rule, required: num === '1' ? ['userJobId'] : [] }, isFieldOn);
  }
  return withConfiguredFields(rule, isFieldOn);
};

// Map UI/codeContent names → fields-state names so required-field lookups can read either form
const FIELD_TO_STATE_KEY = {
  userJobId: 'jobId',
  userPhases: 'phase',
  userCostType: 'costType',
  userCostId: 'costId',
  GlAccount: 'glAccount'
};

// Aliases for fields that have multiple naming conventions across the codebase
// (codeContent name vs fields-state key). Used so VISTA_TYPE_RULES can list either
// form and still match the validator's `name` argument.
const FIELD_ALIASES = [
  ['userJobId', 'jobId'],
  ['userPhases', 'phase'],
  ['userCostType', 'costType'],
  ['userCostId', 'costId'],
  ['GlAccount', 'glAccount']
];

// Returns true if `name` is in `ruleList`, treating aliases as equivalent
// (e.g. 'GlAccount' matches a rule listing 'glAccount' and vice versa).
const ruleListIncludes = (ruleList, name) => {
  if (!ruleList || !name) return false;
  if (ruleList.includes(name)) return true;
  const aliasGroup = FIELD_ALIASES.find((g) => g.includes(name));
  if (!aliasGroup) return false;
  return aliasGroup.some((alias) => ruleList.includes(alias));
};

const fieldHasValue = (fields, fieldName) => {
  if (hasVistaValue(fields?.[fieldName])) return true;
  const stateKey = FIELD_TO_STATE_KEY[fieldName];
  if (stateKey && hasVistaValue(fields?.[stateKey])) return true;
  // Also try alias group (e.g. checking 'glAccount' should look at 'GlAccount' too)
  const aliasGroup = FIELD_ALIASES.find((g) => g.includes(fieldName));
  if (aliasGroup) {
    return aliasGroup.some((alias) => hasVistaValue(fields?.[alias]));
  }
  return false;
};

// True if the field (or any of its aliases) exists as a key on the fields object.
// Header fields (codingfieldStates) and line-item fields (buildVistaLineItemFields)
// have different shapes — e.g. itemReference exists only on line items. Used to
// filter type-rule required fields to those applicable at the current level.
const fieldHasKey = (fields, fieldName) => {
  if (!fields) return false;
  if (fieldName in fields) return true;
  const stateKey = FIELD_TO_STATE_KEY[fieldName];
  if (stateKey && stateKey in fields) return true;
  const aliasGroup = FIELD_ALIASES.find((g) => g.includes(fieldName));
  if (aliasGroup) {
    return aliasGroup.some((alias) => alias in fields);
  }
  return false;
};

/* ───────────────────────────────────────────────────
   3.  FIELD DISABLED STATE (Vista WO logic)
   ─────────────────────────────────────────────────── */

/**
 * Pure flow-based field-disabling for Vista. Does NOT gate on the company's
 * erpValidations permission — that decision belongs to the caller, because the
 * two coding levels are gated differently:
 *   - Header coding (coddingFields): disabling applies only when the company has
 *     ERP coding enabled (erpValidations || the `type` field).
 *   - Line-item coding (lineItems / bottomDrawer): disabling always applies.
 *
 *  - When WO is selected → only WO-related fields are enabled; others disabled.
 *  - When Job is selected → only Job-related fields are enabled; WO fields disabled.
 *  - When nothing is selected → all fields enabled except scope (needs WO first).
 */
export const isFieldDisabledByErp = (fieldName, fields, erpType, typeFieldEnabled = false) => {
  if (!isViewPointErp(erpType)) return false;

  // With the `type` field enabled, no field disabling — the line type decides what is
  // required and the user can enter any field freely.
  if (typeFieldEnabled || fields?.typeFieldEnabled) return false;

  const hasValue = (v) => v && String(v).trim() !== '';

  const hasJob = hasValue(fields?.jobId);
  const hasPhase = hasValue(fields?.phase);
  const hasWorkOrder = hasValue(fields?.workOrder);
  const hasScope = hasValue(fields?.scope);
  const isJobFlow = hasJob && hasPhase;
  const isWOFlow = hasWorkOrder && hasScope;

  if (isJobFlow) {
    if (fieldName === 'GlAccount' || fieldName === 'workOrder' || fieldName === 'scope') {
      return true;
    }
  }

  if (isWOFlow) {
    if (
      fieldName === 'userJobId' ||
      fieldName === 'jobId' ||
      fieldName === 'userPhases' ||
      fieldName === 'phase' ||
      fieldName === 'GlAccount'
    ) {
      return true;
    }
  }

  return false;
};

/* ───────────────────────────────────────────────────
   4.  ACCOUNT-DETAIL (HEADER) VALIDATION PER ERP
   ─────────────────────────────────────────────────── */

const isValueNotEmpty = (value) => {
  // lodash isEmpty treats primitives (numbers/booleans) as empty, so a numeric
  // value like an auto-populated itemReference (1) would falsely fail. Treat any
  // finite number as non-empty and skip the collection-based isEmpty check.
  if (typeof value === 'number') return !isNaN(value);
  return (
    value !== null &&
    !isEmpty(value) &&
    value !== '-' &&
    value !== 'N/A' &&
    String(value).trim() !== ''
  );
};

/**
 * Vista header-level coding field validation.
 * - With the `type` field enabled: type-based rules (VISTA_TYPE_RULES) are the single
 *   source of truth
 * - With it disabled:
 *   - If Job Id is selected → Phase and Cost Type are required
 *   - If Work Order is selected → Scope and Cost Type are required
 *   - If Scope is selected → Work Order and Cost Type are required
 */

const validateVistaAccountDetails = (value, name, fields) => {
  const res = { status: true, message: '' };
  const hasValue = (v) => v && String(v)?.trim() !== '';

  // The type field is required whenever it is enabled
  if (fields?.typeFieldEnabled && name === 'type' && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
    return res;
  }

  // Type-based validation (replaces flow-based logic when permission is on and a Type is selected)
  if (fields?.typeFieldEnabled) {
    const typeRule = getVistaTypeRule(fields?.type, fields?.vistaBatchType, fields?.isFieldOn);
    if (typeRule) {
      if (ruleListIncludes(typeRule.required, name) && !isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name, 'viewPointVista');
        return res;
      }
      // If field is in notAllowed list, it should be empty — no error (just ignored)
      return res;
    }
    // Type field enabled but nothing selected yet — only 'type' itself is required (handled above)
    return res;
  }

  const hasJob = hasValue(fields?.jobId);
  const hasWorkOrder = hasValue(fields?.workOrder);
  const hasScope = hasValue(fields?.scope);
  // The other half of a flow is only required while the company still carries it — a
  // field switched off has no input to fill in, so requiring it would block Verify All
  // with nothing to fix (same reason as withConfiguredFields).
  const fieldOn = (fieldName) => isRuleFieldOn(fields?.isFieldOn, fieldName);

  // If Job Id is selected → Phase and Cost Type are required
  if (hasJob) {
    if (
      (name === 'userPhases' || name === 'userCostType') &&
      fieldOn(name) &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    return res; // Job flow active; do not also require WO/Scope fields
  }

  // If Work Order is selected → Scope and Cost Type are required
  if (hasWorkOrder) {
    if ((name === 'scope' || name === 'userCostType') && fieldOn(name) && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    return res;
  }

  // If Scope is selected → Work Order and Cost Type are required
  if (hasScope) {
    if (
      (name === 'workOrder' || name === 'userCostType') &&
      fieldOn(name) &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    return res;
  }

  return res;
};

/** Foundation header-level coding field validation */
const validateFoundationAccountDetails = (
  value,
  name,
  annotationType,
  isConstructionCompany,
  fields
) => {
  const res = { status: true, message: '' };

  // Normalize VendorNumber — field name can be 'VendorNumber' (capital) or 'vendorNumber' (lowercase)
  const isVendorNumberField = name === 'VendorNumber' || name === 'vendorNumber';

  if (annotationType === 'Po' && isConstructionCompany) {
    if (['userJobId', 'userCostId', 'userCostType', 'GlAccount'].includes(name)) {
      if (!isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name, 'Foundation');
      }
    }
    if (isVendorNumberField && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
  } else if (annotationType === 'G702' && isConstructionCompany) {
    if (name === 'userJobId' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    if (isVendorNumberField && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
  } else if (annotationType === 'Po' && !isConstructionCompany) {
    if (name === 'GlAccount' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    if (isVendorNumberField && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
  } else {
    // All other Foundation doc types
    if (isVendorNumberField && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
  }
  return res;
};

/** QuickBooks Desktop header-level coding field validation */
const validateQBDAccountDetails = (value, name, annotationType, isConstructionCompany, fields) => {
  const res = { status: true, message: '' };

  if (isConstructionCompany && ['Invoice', 'Receipt', 'G702', 'Po'].includes(annotationType)) {
    // item/costId required when jobId present
    if (
      name === 'userCostId' &&
      fields?.jobId !== null &&
      String(fields?.jobId).trim() !== '' &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage('userCostId');
    }
    if (
      name === 'GlAccount' &&
      (fields?.jobId === null || String(fields?.jobId).trim() === '') &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage('GlAccount');
    }
    if (name === 'VendorNumber' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    if (name === 'userCostType' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
  } else if (['Invoice', 'Receipt', 'G702'].includes(annotationType)) {
    if (name === 'GlAccount' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage('GlAccount');
    }
    if (name === 'VendorNumber' || name === 'vendorNumber') {
      if (!isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name);
      }
    }
  } else if (annotationType === 'Po' && !isConstructionCompany) {
    if (name === 'GlAccount' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage('GlAccount');
    }
    if (name === 'VendorNumber' || name === 'vendorNumber') {
      if (!isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name);
      }
    }
  }
  return res;
};

/** QuickBooks Online header-level coding field validation */
const validateQBOAccountDetails = (value, name, annotationType) => {
  const res = { status: true, message: '' };
  if (['Invoice', 'Receipt', 'G702'].includes(annotationType)) {
    if (name === 'GlAccount' && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage('GlAccount');
    }
    if (name === 'VendorNumber' || name === 'vendorNumber') {
      if (!isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name);
      }
    }
  }
  return res;
};

/* ───────────────────────────────────────────────────
   5.  LINE-ITEM VALIDATION PER ERP
   ─────────────────────────────────────────────────── */

/**
 * Vista line-item validation.
 * - Description and Amount are always required (Quantity for DeliveryTicket)
 * - If Job Id is selected → Phase and Cost Type are required
 * - If Work Order is selected → Scope and Cost Type are required
 */
const validateVistaLineItems = (name, value, annotationType, fields) => {
  const res = { status: true, message: '' };

  if (annotationType === 'DeliveryTicket') {
    if ((name === 'Description' || name === 'Quantity') && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  // Tax Code / Tax Type are orthogonal to job/WO/GL flows — they depend solely on
  // whether the row's tax column has a non-zero value. Handled up-front so they
  // short-circuit cleanly without interacting with type-rule or flow validation below.
  if (name === 'tax_code' || name === 'tax_type') {
    const taxRaw = fields?.tax;
    const taxNum = parseFloat(String(taxRaw || '')?.replace(/[^0-9.-]+/g, ''));
    const hasTaxValue =
      taxRaw !== null &&
      taxRaw !== undefined &&
      String(taxRaw)?.trim() !== '' &&
      String(taxRaw)?.trim() !== '-' &&
      !isNaN(taxNum) &&
      taxNum !== 0;
    // Tax Type 2 (use / self-assessed tax) forces Tax Code even when the row carries no tax
    // amount: the vendor does not charge tax so `tax` is often 0/blank, yet Vista still needs
    // the code to self-assess. Match the leading integer so '2' and '2 - Use Tax' both count.
    const isUseTaxRow = parseInt(String(fields?.tax_type ?? '').trim(), 10) === 2;
    const isRequired = hasTaxValue || (name === 'tax_code' && isUseTaxRow);
    if (isRequired && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  // Base required fields for all Vista doc types
  if ((name === 'Description' || name === 'Amount') && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
  }

  if (fields?.typeFieldEnabled && name === 'type' && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
    return res;
  }

  if (fields?.typeFieldEnabled) {
    const typeRule = getVistaTypeRule(fields?.type, fields?.vistaBatchType, fields?.isFieldOn);
    if (typeRule) {
      if (ruleListIncludes(typeRule.required, name) && !isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name, 'viewPointVista');
      }
      return res;
    }
    // Type field enabled but nothing selected yet — only 'type' itself is required (handled above)
    return res;
  }

  const hasValue = (v) => v && String(v)?.trim() !== '';
  const hasJob = hasValue(fields?.jobId);
  const hasWorkOrder = hasValue(fields?.workOrder);
  const hasScope = hasValue(fields?.scope);
  // Only enforce the fields the company still carries — a column switched off in
  // Document Configurations isn't on the table to be filled in.
  const fieldOn = (fieldName) => isRuleFieldOn(fields?.isFieldOn, fieldName);

  if (hasJob) {
    if (
      (name === 'userPhases' || name === 'userCostType') &&
      fieldOn(name) &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    return res; // Job flow active; do not also require WO/Scope fields
  }

  if (hasWorkOrder) {
    if ((name === 'scope' || name === 'userCostType') && fieldOn(name) && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    return res;
  }

  if (hasScope) {
    if (
      (name === 'workOrder' || name === 'userCostType') &&
      fieldOn(name) &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    return res;
  }

  // If neither Job nor Work Order flow is selected → GL Account is required
  if (!hasJob && !hasWorkOrder) {
    if (
      ruleListIncludes(['GlAccount', 'glAccount'], name) &&
      fieldOn('GlAccount') &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
  }

  return res;
};

/** Foundation line-item validation */
const validateFoundationLineItems = (
  name,
  value,
  annotationType,
  isConstructionCompany,
  fields
) => {
  const res = { status: true, message: '' };

  if (annotationType === 'DeliveryTicket') {
    if ((name === 'Description' || name === 'Quantity') && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if (annotationType === 'Po' && isConstructionCompany) {
    if (
      ['userCostId', 'Description', 'userCostType', 'GlAccount', 'userJobId', 'Amount'].includes(
        name
      ) &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name, 'Foundation');
    }
    return res;
  }

  if (annotationType === 'G702' && isConstructionCompany) {
    if (['Description', 'Amount', 'userJobId'].includes(name) && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if (annotationType === 'Po' && !isConstructionCompany) {
    if (['Description', 'Amount', 'GlAccount'].includes(name) && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  // Foundation Invoice/Receipt: Description/Amount required; Cost Id and GL Account
  // form an either/or pair on EACH line item — filling one clears the required state
  // on the other for that same row. The either/or pair is only enforced when the
  // company has the erpValidations permission turned on; without it, the legacy
  // default Foundation rule (Description/Amount required) applies.
  if ((annotationType === 'Invoice' || annotationType === 'Receipt') && fields?.erpValidations) {
    if ((name === 'Description' || name === 'Amount') && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
      return res;
    }
    if (name === 'userCostId' && !isValueNotEmpty(value) && !isValueNotEmpty(fields?.glAccount)) {
      res.status = false;
      res.message = getRequiredMessage('userCostId', 'Foundation');
      return res;
    }
    if (name === 'GlAccount' && !isValueNotEmpty(value) && !isValueNotEmpty(fields?.costId)) {
      res.status = false;
      res.message = getRequiredMessage('GlAccount');
      return res;
    }
    return res;
  }

  // Default Foundation
  if ((name === 'Description' || name === 'Amount') && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
  }

  return res;
};

/** QuickBooks Desktop line-item validation */
const validateQBDLineItems = (name, value, annotationType, isConstructionCompany, jobId) => {
  const res = { status: true, message: '' };

  if (annotationType === 'DeliveryTicket') {
    if ((name === 'Description' || name === 'Quantity') && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if (isConstructionCompany && ['Invoice', 'Receipt', 'G702', 'Po'].includes(annotationType)) {
    const jobPresent = jobId !== null && String(jobId).trim() !== '';
    if (
      name === 'Description' ||
      name === 'Amount' ||
      name === 'userCostType' ||
      (jobPresent && name === 'userCostId') ||
      (jobPresent && name === 'userJobId') ||
      (!jobPresent && name === 'GlAccount')
    ) {
      if (!isValueNotEmpty(value)) {
        res.status = false;
        res.message = getRequiredMessage(name);
      }
    }
    return res;
  }

  if (
    ['Invoice', 'Receipt', 'G702'].includes(annotationType) ||
    (annotationType === 'Po' && !isConstructionCompany)
  ) {
    if (['Description', 'Amount', 'GlAccount'].includes(name) && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if ((name === 'Description' || name === 'Amount') && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
  }
  return res;
};

/** QuickBooks Online line-item validation */
const validateQBOLineItems = (name, value, annotationType, isConstructionCompany) => {
  const res = { status: true, message: '' };

  if (annotationType === 'DeliveryTicket') {
    if ((name === 'Description' || name === 'Quantity') && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if (['Invoice', 'Receipt', 'G702'].includes(annotationType)) {
    if (['Description', 'Amount', 'GlAccount'].includes(name) && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if ((name === 'Description' || name === 'Amount') && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
  }
  return res;
};

/** RentalMan line-item validation */
const validateRentalManLineItems = (name, value, annotationType) => {
  const res = { status: true, message: '' };

  if (annotationType === 'Invoice' || annotationType === 'Estimate') {
    if (
      (name === 'Description' ||
        name === 'Amount' ||
        (name === 'GlAccount' && annotationType === 'Invoice') ||
        name === 'Product_Code') &&
      !isValueNotEmpty(value)
    ) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  if (annotationType === 'DeliveryTicket') {
    if ((name === 'Description' || name === 'Quantity') && !isValueNotEmpty(value)) {
      res.status = false;
      res.message = getRequiredMessage(name);
    }
    return res;
  }

  // G702 / Receipt / default
  if (['Description', 'Amount', 'GlAccount'].includes(name) && !isValueNotEmpty(value)) {
    res.status = false;
    res.message = getRequiredMessage(name);
  }
  return res;
};

/* ───────────────────────────────────────────────────
   6.  REQUIRED-MESSAGE HELPER
   ─────────────────────────────────────────────────── */

const REQUIRED_MESSAGES = {
  userJobId: 'Job Id must not be empty',
  userCostId: 'Cost Id must not be empty',
  userCostType: 'Cost type must not be empty',
  GlAccount: 'Gl account must not be empty',
  userPhases: 'Phase must not be empty',
  VendorNumber: 'Vendor number must not be empty and it must be associated with Vendor',
  vendorNumber: 'Vendor number must not be empty and it must be associated with Vendor',
  workOrder: 'Work Order must not be empty',
  scope: 'Scope must not be empty',
  equipmentNo: 'Equipment No must not be empty',
  serviceCode: 'Service Code must not be empty',
  Description: 'Description must not be empty',
  Amount: 'Amount must not be empty',
  Quantity: 'Quantity must not be empty',
  Product_Code: 'Product code must not be empty',
  itemNumber: 'Item must not be empty',
  itemReference: 'Item Reference must not be empty',
  type: 'Type must not be empty',
  tax_code: 'Tax Code must not be empty',
  tax_type: 'Tax Type must not be empty'
};

const getRequiredMessage = (name, erpType) => {
  // Foundation / VP Vista surface Cost Id as "Cost Code" on the UI (BE key stays
  // userCostId), so their required-field message must match the label the user sees.
  if (name === 'userCostId' && (erpType === 'Foundation' || erpType === 'viewPointVista'))
    return 'Cost Code must not be empty';
  return REQUIRED_MESSAGES[name] || `${name} must not be empty`;
};

/* ───────────────────────────────────────────────────
   7.  UNIFIED PUBLIC API
   ─────────────────────────────────────────────────── */

/**
 * Job Id is required on a delivery ticket once the company turns on
 * "Require coding for Delivery Ticket". Returns an error object, or null when
 * the rule does not apply.
 *
 * Deliberately NOT part of `validateAccountDetailsByErp`'s per-ERP switch. This
 * is a company setting, not an ERP behaviour: it has to mean the same thing
 * everywhere. Inside that switch it would have to be written four times, and it
 * would silently never fire on the ERPs that fall through to the default no-op
 * branch (NetSuite, Sage Intacct, Acumatica, RentalMan, …) — the permission
 * would read as on and do nothing.
 *
 * The job is the whole point of the permission: it is what lets a delivery
 * ticket be matched to an invoice by something stronger than a vendor and a
 * date. A ticket approved without one can never be matched, which is why this
 * blocks approval rather than merely warning.
 */
export const getDeliveryTicketJobIdError = (
  name,
  value,
  annotationType,
  deliveryTicketCodingPermission
) => {
  if (!deliveryTicketCodingPermission) return null;
  if (annotationType !== 'DeliveryTicket') return null;
  if (name !== 'userJobId') return null;
  if (isValueNotEmpty(value)) return null;
  return { status: false, message: getRequiredMessage('userJobId') };
};

/**
 * Validate a header-level (account-detail / coding) field.
 * Drop-in replacement for the old `validateAccountDetails`.
 */
export const validateAccountDetailsByErp = (
  value,
  name,
  annotationType,
  erpType,
  isConstructionCompany,
  fields
) => {
  switch (erpType) {
    case 'viewPointVista':
    case 'viewPointSpectrum':
      return validateVistaAccountDetails(value, name, fields, isConstructionCompany);

    case 'Foundation':
      return validateFoundationAccountDetails(
        value,
        name,
        annotationType,
        isConstructionCompany,
        fields
      );

    case 'QuickBooksDesktop':
      return validateQBDAccountDetails(value, name, annotationType, isConstructionCompany, fields);

    case 'QuickBooksOnline':
      return validateQBOAccountDetails(value, name, annotationType);

    default:
      return { status: true, message: '' };
  }
};

/**
 * Validate a line-item field.
 * Drop-in replacement for the old `validateLineItems`.
 */
export const validateLineItemsByErp = (
  name,
  value,
  annotationType,
  erpType,
  isConstructionCompany,
  jobIdOrFields
) => {
  // jobIdOrFields can be either a string (jobId) for backward compat, or the full fields object
  const fields =
    typeof jobIdOrFields === 'object' && jobIdOrFields !== null
      ? jobIdOrFields
      : { jobId: jobIdOrFields };
  const jobId = fields?.jobId ?? null;

  switch (erpType) {
    case 'viewPointVista':
    case 'viewPointSpectrum':
      return validateVistaLineItems(name, value, annotationType, fields);

    case 'Foundation':
      return validateFoundationLineItems(
        name,
        value,
        annotationType,
        isConstructionCompany,
        fields
      );

    case 'QuickBooksDesktop':
      return validateQBDLineItems(name, value, annotationType, isConstructionCompany, jobId);

    case 'QuickBooksOnline':
      return validateQBOLineItems(name, value, annotationType, isConstructionCompany);

    case 'RentalMan':
      return validateRentalManLineItems(name, value, annotationType);

    default: {
      // Generic fallback
      const res = { status: true, message: '' };
      if (annotationType === 'DeliveryTicket') {
        if ((name === 'Description' || name === 'Quantity') && !isValueNotEmpty(value)) {
          res.status = false;
          res.message = getRequiredMessage(name);
        }
      } else {
        if ((name === 'Description' || name === 'Amount') && !isValueNotEmpty(value)) {
          res.status = false;
          res.message = getRequiredMessage(name);
        }
      }
      return res;
    }
  }
};

/* ───────────────────────────────────────────────────
   8.  ERP-TYPE HELPERS
   ─────────────────────────────────────────────────── */

/**
 * Returns true if the ERP type is Vista or Spectrum (ViewPoint family).
 * Use this instead of scattering raw string comparisons across the codebase.
 */
export const isViewPointErp = (erpType) => erpType === 'viewPointVista';

/**
 * Returns true if the ERP type skips vendor name length validation.
 * Vista has no vendor name length limit, so it is exempt alongside
 * Foundation and RentalMan.
 */
export const isVendorLengthExempt = (erpType) =>
  erpType === 'Foundation' || erpType === 'RentalMan' || isViewPointErp(erpType);

/* ───────────────────────────────────────────────────
   9.  VISTA FLOW HELPERS (Single Source of Truth)
   ─────────────────────────────────────────────────── */

/**
 * Checks if a value is non-empty (common helper used by all Vista flow checks).
 */
const hasVistaValue = (v) => v && String(v).trim() !== '' && v !== '-';

/**
 * For VP Vista: Check if at least one valid flow is satisfied for a given fields object.
 *
 * When the `type` field is enabled, the line "type" (1–8) is the single source of truth:
 * each type declares its own required fields (see VISTA_TYPE_RULES), so the generic
 * Job/WO/GL flow check does not apply. With `type` disabled, valid flows are:
 *   1. Job Flow: jobId + phase
 *   2. Work Order Flow: workOrder + scope
 *   3. GL Flow: glAccount / GlAccount
 *
 * Works for BOTH header-level fields and per-line-item fields (built via buildVistaLineItemFields).
 *
 * Both modes only ever demand fields the company still carries. This check is what gates
 * Verify All, and a field switched off in Document Configurations has no input anywhere on
 * the canvas — so enforcing it disables the button with nothing the user can do about it
 * (job off + Type 1-Job was exactly that). The predicate is optional: without one every
 * field is treated as carried, which is the behaviour before the fields were configurable.
 *
 * @param {Object} fields - The coding fields object
 * @param {boolean} typeFieldEnabled - Whether the Vista `type` field is enabled
 * @param {string} [vistaBatchType] - Company batch type ('Approved' | 'UnApproved'); 'UnApproved'
 *   relaxes Job (Type 1) to Job Id only and drops every requirement on the other types
 *   (see getVistaTypeRule).
 * @param {Function} [isFieldOn] - `isFieldOn` from useFieldConfig; may also travel on `fields`.
 * @returns {boolean} - True if at least one flow is satisfied
 */
export const isVistaFlowSatisfied = (
  fields,
  typeFieldEnabled = false,
  vistaBatchType = null,
  isFieldOn = null
) => {
  if (!fields) return false;

  const fieldConfig = isFieldOn ?? fields?.isFieldOn;
  const fieldOn = (fieldName) => isRuleFieldOn(fieldConfig, fieldName);

  const hasTypeField = typeFieldEnabled || fields?.typeFieldEnabled;
  if (hasTypeField) {
    const typeRule = getVistaTypeRule(
      fields?.type,
      vistaBatchType ?? fields?.vistaBatchType,
      fieldConfig
    );
    if (!typeRule) return false;
    // Only enforce required fields that exist at this level (header vs line-item).
    // e.g. Inventory's `itemReference` lives on the line item, not the header — the
    // header has nothing to enforce, so it's satisfied as soon as Type is set.
    const applicableRequired = (typeRule?.required || [])?.filter((f) => fieldHasKey(fields, f));
    if (applicableRequired.length === 0) return true;
    return applicableRequired.every((reqField) => fieldHasValue(fields, reqField));
  }

  // A pair whose second half is switched off collapses to the half that is left, and a
  // flow whose own field is off is not a flow this company can code at all. With all
  // three gone there is nothing left to satisfy, so nothing is missing.
  const jobFlowAvailable = fieldOn('userJobId');
  const woFlowAvailable = fieldOn('workOrder');
  const glFlowAvailable = fieldOn('GlAccount');
  if (!jobFlowAvailable && !woFlowAvailable && !glFlowAvailable) return true;

  const isJobFlow =
    jobFlowAvailable &&
    hasVistaValue(fields?.jobId) &&
    (!fieldOn('userPhases') || hasVistaValue(fields?.phase));
  const isWOFlow =
    woFlowAvailable &&
    hasVistaValue(fields?.workOrder) &&
    (!fieldOn('scope') || hasVistaValue(fields?.scope));
  const isGLFlow =
    glFlowAvailable && (hasVistaValue(fields?.glAccount) || hasVistaValue(fields?.GlAccount));

  return isJobFlow || isWOFlow || isGLFlow;
};

/**
 * For VP Vista: Check if at least one valid flow is satisfied for EACH line item.
 * Iterates through the line items obj (arrays keyed by field name) and checks each index.
 * @param {Object} lineItemsObj - The line items object with arrays of values (e.g. obj)
 * @param {boolean} typeFieldEnabled - Whether the Vista `type` field is enabled
 * @param {string} [vistaBatchType] - Company batch type; 'UnApproved' relaxes Job (Type 1) to Job Id
 *   only and drops every requirement on the other types.
 * @param {Function} [isFieldOn] - `isFieldOn` from useFieldConfig; fields the company switched
 *   off are not required of any row (see isVistaFlowSatisfied).
 * @returns {boolean} - True if ALL line items satisfy at least one flow (or if there are no line items)
 */
export const isVistaLineItemFlowSatisfied = (
  lineItemsObj,
  typeFieldEnabled = false,
  vistaBatchType = null,
  isFieldOn = null
) => {
  if (!lineItemsObj || Object.keys(lineItemsObj)?.length === 0) return true;

  const descriptionValues = lineItemsObj['Description'] || [];
  const lineItemCount = descriptionValues?.length;
  if (lineItemCount === 0) return true;

  for (let i = 0; i < lineItemCount; i++) {
    const rowFields = buildVistaLineItemFields(lineItemsObj, i);
    if (!isVistaFlowSatisfied(rowFields, typeFieldEnabled, vistaBatchType, isFieldOn)) {
      return false;
    }
  }
  return true;
};

/**
 * Build a normalized fields object for a specific line item row from the line items obj.
 * Maps line-item key names (userJobId, userPhases, etc.) to the standard field names
 * (jobId, phase, etc.) used by isFieldDisabledByErp, validateLineItemsByErp, and isVistaFlowSatisfied.
 *
 * @param {Object} lineItemsObj - The line items object with arrays (e.g. obj)
 * @param {number} index - The row index
 * @returns {Object} - Normalized fields object for that row
 */
export const buildVistaLineItemFields = (lineItemsObj, index, extraFields = {}) => {
  return {
    jobId: lineItemsObj?.['userJobId']?.[index] || '',
    phase: lineItemsObj?.['userPhases']?.[index] || '',
    workOrder: lineItemsObj?.['workOrder']?.[index] || '',
    scope: lineItemsObj?.['scope']?.[index] || '',
    glAccount: lineItemsObj?.['GlAccount']?.[index] || '',
    GlAccount: lineItemsObj?.['GlAccount']?.[index] || '',
    costType: lineItemsObj?.['userCostType']?.[index] || '',
    costId: lineItemsObj?.['userCostId']?.[index] || '',
    equipmentNo: lineItemsObj?.['equipmentNo']?.[index] || '',
    serviceCode: lineItemsObj?.['serviceCode']?.[index] || '',
    itemReference: lineItemsObj?.['itemReference']?.[index] || '',
    type: lineItemsObj?.['type']?.[index] || '',
    tax: lineItemsObj?.['tax']?.[index] || '',
    tax_code: lineItemsObj?.['tax_code']?.[index] || '',
    tax_type: lineItemsObj?.['tax_type']?.[index] || '',
    discount: lineItemsObj?.['discount']?.[index] || '',
    ...extraFields
  };
};

/* ───────────────────────────────────────────────────
   10.  VISTA GL DATE — OPEN AP PERIOD WINDOW
   ─────────────────────────────────────────────────── */

/**
 * Read a key from the synced ERP company object regardless of casing — the record mixes
 * Vista's own PascalCase columns (LastMthAPClsd, MaxOpen) with the camelCase fields the
 * sync layer adds (firstOpenMonth), so match on the lowercased key name.
 */
const getCompanyField = (company, key) => {
  if (!company || typeof company !== 'object') return undefined;
  const wanted = String(key).toLowerCase();
  const match = Object.keys(company).find((k) => k.toLowerCase() === wanted);
  return match === undefined ? undefined : company[match];
};

/**
 * Parse an ERP date value into the first day of its month (local time).
 * Handles Date objects, ISO strings ("2026-05-01T00:00:00") and US strings
 * ("05/01/2026"). Y/M are pulled out with a regex first so an ISO date is never
 * shifted a day (and therefore a month) by UTC parsing.
 */
const parseErpMonthStart = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), 1);
  }
  const str = String(value).trim();
  if (!str) return null;

  const iso = str.match(/^(\d{4})-(\d{1,2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, 1);

  const mdy = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (mdy) return new Date(Number(mdy[3]), Number(mdy[1]) - 1, 1);

  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), 1);
};

/**
 * Vista's open AP period window is read straight off the synced ERP company (HQCO)
 * record, which arrives with `firstOpenMonth` / `lastOpenMonth` already resolved:
 *
 *   { ..., LastMthAPClsd: '2024-04-01T00:00:00', MaxOpen: 84,
 *          firstOpenMonth: '2024-05-01T00:00:00', lastOpenMonth: '2031-04-01T00:00:00' }
 *
 * The sync layer derives them from LastMthAPClsd (the last CLOSED month) and MaxOpen
 * (how many months may stay open past it). Those values are the ERP's own answer — the
 * same window the export is judged against — so this reads them rather than re-deriving
 * the arithmetic here, where it could drift from the sync layer's.
 *
 * The synced `erpCompany` payload is an array holding a single company object, so
 * index 0 is the one to read (an object is accepted too).
 *
 * The window is only enforced for Approved batch types. An UnApproved batch is posted
 * in Vista later, against whichever periods are open then, so holding its GL Date to
 * today's window would reject dates that are valid by the time the batch posts. Batch
 * type defaults to Approved, so only an explicit 'UnApproved' lifts the rule.
 *
 * @param {Array|Object} erpCompany - syncedData.erpCompany
 * @param {string} [vistaBatchType] - Company batch type ('Approved' | 'UnApproved');
 *   'UnApproved' switches the window off entirely.
 * @returns {{ firstOpenMonth: Date, lastOpenMonth: Date }|null} null when the rule is
 *   off for this batch type, or the record is missing/unsynced or carries no usable
 *   window — callers must then skip the range check rather than block a GL Date on
 *   data we don't have.
 */
export const getVistaOpenGlMonthRange = (erpCompany, vistaBatchType = null) => {
  if (vistaBatchType === 'UnApproved') return null;

  const company = Array.isArray(erpCompany) ? erpCompany[0] : erpCompany;
  // No company record synced yet ([] / [{}] / undefined) → no window, so the caller
  // leaves GL Date exactly as it behaves without this rule.
  if (isEmpty(company)) return null;

  const firstOpenMonth = parseErpMonthStart(getCompanyField(company, 'firstOpenMonth'));
  const lastOpenMonth = parseErpMonthStart(getCompanyField(company, 'lastOpenMonth'));
  // Blank or inverted pair → nothing trustworthy to enforce, so the rule stays off
  // rather than being enforced against a window we guessed at.
  if (!firstOpenMonth || !lastOpenMonth || lastOpenMonth.getTime() < firstOpenMonth.getTime()) {
    return null;
  }

  return { firstOpenMonth, lastOpenMonth };
};

/** Format a Date as MM/YYYY for GL-period messaging. */
export const formatGlMonth = (date) =>
  date instanceof Date && !isNaN(date.getTime())
    ? `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
    : '';

/**
 * True when the given GL Date falls inside the open AP window (month granularity —
 * Vista GL Dates are always the 1st of a month). Unknown range or unparseable date
 * → true, so this rule only ever fires on data we can actually judge.
 */
export const isGlDateInOpenPeriod = (value, range) => {
  if (!range?.firstOpenMonth || !range?.lastOpenMonth) return true;
  const month = parseErpMonthStart(value);
  if (!month) return true;
  return (
    month.getTime() >= range.firstOpenMonth.getTime() &&
    month.getTime() <= range.lastOpenMonth.getTime()
  );
};

/**
 * Verify that header-level codes match line item codes (for non-Vista ERPs).
 * Returns true if there is a MISMATCH (i.e. codes differ), false if they match or check doesn't apply.
 *
 * @param {Object} lineItems - The line items object with arrays
 * @param {Object} fields - The header-level fields
 * @param {Array} accountCodeArray - The account code array
 * @returns {boolean} - True if mismatch found
 */
export const verifySameCodesForHeaderAndLineItems = (lineItems, fields, accountCodeArray) => {
  if (!lineItems || Object.keys(lineItems)?.length === 0 || isEmpty(accountCodeArray)) return false;

  const fieldToLineItemMap = {
    serviceCode: 'serviceCode',
    equipmentNo: 'equipmentNo',
    glDivision: 'glDivision',
    costType: 'userCostType',
    costId: 'userCostId',
    jobId: 'userJobId',
    workOrder: 'workOrder',
    scope: 'scope',
    phase: 'userPhases',
    glAccount: 'GlAccount',
    division2: 'division2',
    division3: 'division3',
    division4: 'division4'
  };

  return Object.entries(fieldToLineItemMap)?.some(([fieldKey, lineItemKey]) => {
    if (!lineItemKey) return false;
    if (!lineItems[lineItemKey] || lineItems[lineItemKey]?.length !== 1) return false;

    const fieldVal = fields[fieldKey] || '';
    const lineItemVal = lineItems[lineItemKey][0] || '';

    return fieldVal !== lineItemVal;
  });
};
